import type { QueryClient } from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";
import type { LoaderFunction } from "react-router-dom";

import { configureAmplify, orgStorage } from "@/shared/auth";
import {
  resolveExchangeOutcomeView,
  resolveOAuthCallbackView,
  type OAuthCallbackView,
  type OAuthExchangeOutcome,
} from "@/shared/components/OAuthCallback/resolveOAuthCallbackView";
import { SlackService } from "@/shared/domain/slack";
import { orgFromStateToken } from "@/shared/domain/slack/slackOAuthState";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";

export const NO_SESSION_MESSAGE =
  "You are not signed in. Sign in to Roboto, then reload this page to finish connecting.";

/**
 * Cache key for one authorization code's exchange. Deliberately OUTSIDE every domain key
 * factory — it must never move under one, because invalidating this key re-sends a spent
 * code. `orgId` is deliberately absent: the key has to hash identically on every re-entry.
 */
function exchangeQueryKey(code: string, state: string): readonly unknown[] {
  return ["oauthExchange", "slack", code, state];
}

/** What the exchange needs from outside this document. */
export interface SlackOAuthExchangeDeps {
  /** Whether this document holds tokens the exchange can authenticate with. */
  hasSession: () => Promise<boolean>;
  createSlackService: () => Promise<SlackService>;
}

const PRODUCTION_DEPS: SlackOAuthExchangeDeps = {
  hasSession: async () => {
    // The loader runs before any element in its route branch mounts, so nothing has
    // configured Amplify in this document yet.
    await configureAmplify();
    const session = await fetchAuthSession();
    return session.tokens?.idToken !== undefined;
  },
  createSlackService: async () =>
    new SlackService(await makeAuthenticatedHttpClient()),
};

/**
 * Runs the Slack OAuth exchange for the callback route and hands the page its settled outcome.
 *
 * A loader re-runs on every navigation to its route, back/forward included, so the
 * once-per-code guarantee cannot come from the loader itself. It comes from
 * `ensureQueryData` keyed on the code, whose fetcher resolves on failure instead of
 * rejecting: an errored query holds no data, so `ensureQueryData` would call it again on the
 * next entry and spend a code Slack has already refused.
 *
 * The authorization code is single-use, so nothing is sent until a session is confirmed —
 * a loader runs ahead of `AuthenticationLayout`, which therefore cannot gate it. Both the
 * session check and its failure view stay outside the cache, so signing in and reloading
 * still reaches an unspent code.
 *
 * Cache freshness for the connection list is the opener's job: `useConnectSlackPopup`
 * invalidates it when the success message arrives, so nothing here does.
 *
 * @param deps overridden per-key by tests; production reads the session from Amplify and
 * builds a service against the authenticated HTTP client.
 */
export function slackOAuthCallbackLoader(
  queryClient: QueryClient,
  deps: Partial<SlackOAuthExchangeDeps> = {},
): LoaderFunction {
  const { hasSession, createSlackService } = { ...PRODUCTION_DEPS, ...deps };

  return async ({ request }): Promise<OAuthCallbackView> => {
    const params = new URL(request.url).searchParams;
    const state = params.get("state");

    const resolution = resolveOAuthCallbackView({
      code: params.get("code"),
      state,
      providerError: params.get("error"),
      providerErrorDescription: params.get("error_description"),
      // The state token names the org the install started as, and an org switch in any tab
      // between Connect and Slack's redirect changes what this document considers current.
      // Reading it from the token also makes the org a function of the code alone, so a
      // re-entry derives the same org the cached outcome was produced for.
      orgId:
        (state !== null ? orgFromStateToken(state) : undefined) ??
        orgStorage.getCurrentOrg()?.org_id,
    });

    if (resolution.status === "error") {
      return resolution;
    }

    let signedIn = false;
    try {
      signedIn = await hasSession();
    } catch {
      // A session that cannot even be read is not one to spend a single-use code against.
      signedIn = false;
    }
    if (!signedIn) {
      // The query params survive: the code is still unspent, so signing in and reloading
      // completes the install rather than restarting it.
      return {
        status: "error",
        message: NO_SESSION_MESSAGE,
        stripQuery: false,
        paramLevel: false,
      };
    }

    const outcome = await queryClient.ensureQueryData({
      queryKey: exchangeQueryKey(resolution.code, resolution.state),
      queryFn: async (): Promise<OAuthExchangeOutcome> => {
        try {
          const service = await createSlackService();
          await service.completeOAuthFlow(
            { code: resolution.code, state: resolution.state },
            resolution.orgId,
          );
          return { ok: true };
        } catch (error) {
          return { ok: false, error };
        }
      },
      staleTime: Infinity,
      gcTime: Infinity,
    });

    return resolveExchangeOutcomeView(outcome);
  };
}

/** The callback route never revalidates: re-running it would re-derive a spent flow. */
export function shouldRevalidate(): boolean {
  return false;
}
